/**
 * Window-event bridge between the globally-mounted Kai chat provider and the
 * Home dashboard editor. Kai's SSE stream is handled in `KaiChatContext` (which
 * lives above the routed page tree), but the live-preview state lives in
 * `useHomeEditor` on the Home page. Rather than thread refs through context, we
 * relay Kai's home-layout patches as DOM `CustomEvent`s — the same decoupling
 * used for Kai table-preference updates.
 *
 * Because these events are only observed while the Home page is mounted, Kai's
 * layout edits are naturally scoped to `/dashboard`.
 *
 * Direction of each event:
 *   PATCH   / REVIEW   : chat provider → home editor (apply preview, prompt review)
 *   APPROVE / UNDO     : chat review card → home editor (resolve the session)
 *   RESOLVE            : home editor → chat provider (dismiss the review card)
 */
import type { HomeLayoutPatch, HomeLayoutReview } from "@/types/home";

export const KAI_HOME_LAYOUT_PATCH_EVENT = "kai-home-layout-patch";
export const KAI_HOME_LAYOUT_REVIEW_EVENT = "kai-home-layout-review";
export const KAI_HOME_LAYOUT_APPROVE_EVENT = "kai-home-layout-approve";
export const KAI_HOME_LAYOUT_UNDO_EVENT = "kai-home-layout-undo";
export const KAI_HOME_LAYOUT_RESOLVE_EVENT = "kai-home-layout-resolve";

/** How a Kai home-layout session was resolved, relayed back to the chat card. */
export type HomeLayoutResolution = "approved" | "undone";

export function dispatchHomeLayoutPatch(patch: HomeLayoutPatch): void {
  window.dispatchEvent(
    new CustomEvent<HomeLayoutPatch>(KAI_HOME_LAYOUT_PATCH_EVENT, {
      detail: patch,
    }),
  );
}

export function dispatchHomeLayoutReview(review: HomeLayoutReview): void {
  window.dispatchEvent(
    new CustomEvent<HomeLayoutReview>(KAI_HOME_LAYOUT_REVIEW_EVENT, {
      detail: review,
    }),
  );
}

export function requestHomeLayoutApprove(): void {
  window.dispatchEvent(new Event(KAI_HOME_LAYOUT_APPROVE_EVENT));
}

export function requestHomeLayoutUndo(): void {
  window.dispatchEvent(new Event(KAI_HOME_LAYOUT_UNDO_EVENT));
}

export function dispatchHomeLayoutResolved(resolution: HomeLayoutResolution): void {
  window.dispatchEvent(
    new CustomEvent<HomeLayoutResolution>(KAI_HOME_LAYOUT_RESOLVE_EVENT, {
      detail: resolution,
    }),
  );
}
